import { useMemo, useState } from 'react';
import { ClipboardPlus, MapPinPlus, Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ActionBar } from 'components/ActionBar';
import { FilterBar, type FilterConfig } from 'components/FilterBar';
import { confirmPopup } from 'components/Popups/Confirm';
import { TotemInput } from 'components/TotemInput';
import { Header } from 'components/Header';
import { HeaderTitle } from 'components/Header/HeaderTitle';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { CreateSiteDeliveryPopup } from 'pages/Deliveries/Popups/CreateSiteDeliveryPopup';
import { DateNavigator } from 'pages/DynamicDeliveryPlanning/components/DateNavigator';
import { SegmentedControl } from 'components/SegmentedControl';

import { Button } from '@/components/ui/button';

import {
    DeliveryDestinationType,
    DeliveryState,
    TaskState,
    TourState,
    useGetDeliveryPeopleQuery,
    useGetTourPlanningForDateQuery,
    TotadminPermission,
} from 'data/__generated__';
import { transformForComparison } from 'helpers/textHelper';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';
import { MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS } from 'pages/MicrostoreColumnTemplates/constants/types';

import {
    DESTINATION_BY_DELIVERY_TYPE,
    TOUR_LOAD_HINT,
    deliveryColumnTypes,
    deliveryLabel,
    deliveryPersonDayLoads,
    formatMinutes,
    getStopState,
    tourStateOptions,
    type Tour,
    type TourDelivery,
    type TourTask,
} from './helpers';
import { useTourActions } from './hooks/useTourActions';
import { TaskPopup } from './components/TaskPopup';
import { TourEditor } from './components/TourEditor';
import { TourTimeline, type DraftTour } from './components/TourTimeline';
import { TourList } from './components/TourList';
import type { ApproOption } from './components/ApproPicker';

type ViewMode = 'timeline' | 'carte' | 'tableau';
const VIEW_MODES: ViewMode[] = ['timeline', 'carte', 'tableau'];

const FAILED_STATES = [DeliveryState.NotDelivered, DeliveryState.Aborted];

const countKpis = (deliveries: TourDelivery[]) => ({
    total: deliveries.length,
    inProgress: deliveries.filter((delivery) => delivery.state === DeliveryState.InProgress).length,
    delivered: deliveries.filter((delivery) => getStopState(delivery) === DeliveryState.Delivered).length,
    failed: deliveries.filter((delivery) => FAILED_STATES.includes(getStopState(delivery))).length,
});

type KpiTone = 'inprogress' | 'delivered' | 'failed' | 'unassigned';
const KPI_TONE: Record<KpiTone, string> = {
    inprogress: '#2563eb',
    delivered: '#16a34a',
    failed: '#dc2626',
    unassigned: '#d97706',
};

const EMPTY_TOURS: Tour[] = [];
const EMPTY_DELIVERIES: TourDelivery[] = [];
const EMPTY_TASKS: TourTask[] = [];

const tasksOfPlanning = ({ tours, unassignedTasks }: { tours: Tour[]; unassignedTasks: TourTask[] }): TourTask[] => [
    ...tours.flatMap((tour) => tour.stops.flatMap((stop) => (stop.__typename === 'Task' ? [stop] : []))),
    ...unassignedTasks,
];

// An archived tour left the operations: the Statut filter leaves it out unless asked for.
const DEFAULT_TOUR_STATES: string[] = tourStateOptions()
    .map(({ value }) => value)
    .filter((state) => state !== TourState.Archived);

const toOptions = (labels: Record<string, string>) =>
    Object.entries(labels).map(([value, label]) => ({ value, label }));

const STOP_TYPE_LABELS = { delivery: 'Livraisons', task: 'Tâches' };

const FILTERS: FilterConfig[] = [
    {
        key: 'state',
        title: 'Statut',
        type: 'multi-select',
        options: tourStateOptions(),
    },
    { key: 'stopType', title: "Type d'arrêt", type: 'multi-select', options: toOptions(STOP_TYPE_LABELS) },
    {
        key: 'columnType',
        title: 'Colonnes',
        type: 'multi-select',
        options: toOptions(MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS),
    },
    { key: 'flow', title: 'Flux', type: 'multi-select', options: toOptions(BUNDLE_TYPES_LABELS) },
    {
        key: 'deliveryType',
        title: 'Type de livraison',
        type: 'multi-select',
        options: toOptions({
            [DeliveryDestinationType.Store]: 'Store',
            [DeliveryDestinationType.Freefood]: 'Freefood',
        }),
    },
];

export const Tournees = () => {
    const navigate = useNavigate();
    // Date lives in the URL (/tours/:selectedDate), like the dynamic delivery planning — shareable & refresh-safe.
    const { selectedDate: selectedDateFromUrl } = useParams<{ selectedDate?: string }>();
    const [focusedStopId, setFocusedStopId] = useState<string | null>(null);
    const [draftTours, setDraftTours] = useState<DraftTour[]>([]);
    const [isCreateSiteDeliveryPopupOpen, setIsCreateSiteDeliveryPopupOpen] = useState(false);
    const [openedTask, setOpenedTask] = useState<TourTask | 'new' | null>(null);
    // Filters live in the query string, like the deliveries page's — shareable & refresh-safe.
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDate = useMemo(
        () => (selectedDateFromUrl ? dateFromString(selectedDateFromUrl) : new Date()),
        [selectedDateFromUrl],
    );
    const birthdayDate = formatDateAsAnniversary({ dateTime: selectedDate });
    const setSelectedDate = (date: Date): void => {
        void navigate({
            pathname: `/tours/${formatDateAsAnniversary({ dateTime: date })}`,
            search: searchParams.toString(), // the filters follow the dispatcher from one day to the next
        });
    };

    const { data, refetch } = useGetTourPlanningForDateQuery({
        variables: { birthdayDate },
        fetchPolicy: 'cache-and-network',
    });
    const { data: peopleData } = useGetDeliveryPeopleQuery();
    const actions = useTourActions({ birthdayDate, refetch });

    const tours = data?.tourPlanningForDate.tours ?? EMPTY_TOURS;
    const unassignedDeliveries = data?.tourPlanningForDate.unassignedDeliveries ?? EMPTY_DELIVERIES;

    const unassignedTasks = data?.tourPlanningForDate.unassignedTasks ?? EMPTY_TASKS;

    const allDeliveries = useMemo(
        () => [...tours.flatMap((tour) => tour.deliveries), ...unassignedDeliveries],
        [tours, unassignedDeliveries],
    );
    const allTasks = useMemo(() => tasksOfPlanning({ tours, unassignedTasks }), [tours, unassignedTasks]);
    // The window edits a copy: read it fresh, or saving it would undo what the driver did since the page loaded.
    const openTask = (task: TourTask): void => {
        void refetch().then(
            ({ data: fresh }) =>
                setOpenedTask(tasksOfPlanning(fresh.tourPlanningForDate).find(({ _id }) => _id === task._id) ?? task),
            () => setOpenedTask(task),
        );
    };
    const kpis = useMemo(() => countKpis(allDeliveries), [allDeliveries]);
    const finishedTaskCount = allTasks.filter(({ state }) => state === TaskState.Finished).length;

    // In the URL like the filters, so the view follows the dispatcher from one day to the next too.
    const view = VIEW_MODES.find((mode) => mode === searchParams.get('view')) ?? 'timeline';
    const searchValue = searchParams.get('searchValue') ?? '';
    const columnTypes = searchParams.getAll('columnType');
    const flows = searchParams.getAll('flow');
    const deliveryTypes = searchParams.getAll('deliveryType');
    const stopTypes = searchParams.getAll('stopType');
    // No `state` in the URL means every state but « Archivée ».
    const stateParams = searchParams.getAll('state');
    const states = stateParams.length > 0 ? stateParams : DEFAULT_TOUR_STATES;
    const stopFilterState: Record<string, unknown> = {
        searchValue: searchValue || undefined,
        columnType: columnTypes.length > 0 ? columnTypes : undefined,
        flow: flows.length > 0 ? flows : undefined,
        deliveryType: deliveryTypes.length > 0 ? deliveryTypes : undefined,
        stopType: stopTypes.length > 0 ? stopTypes : undefined,
    };
    const filterState = { ...stopFilterState, state: states };
    // The state filter picks the tours, the others dim the deliveries.
    const timelineTours = tours.filter((tour) => states.includes(tour.state));

    const lowerSearch = transformForComparison(searchValue.trim());
    const matchesFilters = (delivery: TourDelivery): boolean =>
        transformForComparison(deliveryLabel(delivery)).includes(lowerSearch) &&
        (columnTypes.length === 0 || deliveryColumnTypes(delivery).some((type) => columnTypes.includes(type))) &&
        (flows.length === 0 || delivery.boxes.some((box) => flows.includes(box.type))) &&
        (deliveryTypes.length === 0 || deliveryTypes.includes(DESTINATION_BY_DELIVERY_TYPE[delivery.type] ?? ''));
    // Null when nothing is filtered: the timeline then dims nothing. The delivery filters leave the tasks alone.
    const matchingStopIds = Object.values(stopFilterState).some(Boolean)
        ? new Set(
              [
                  ...(stopTypes.length === 0 || stopTypes.includes('delivery')
                      ? allDeliveries.filter(matchesFilters)
                      : []),
                  ...(stopTypes.length === 0 || stopTypes.includes('task') ? allTasks : []),
              ].map(({ _id }) => _id),
          )
        : null;

    function updateFilter(key: string, value: unknown): void {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(key);
        [value]
            .flat()
            .filter(Boolean)
            .forEach((item) => newParams.append(key, String(item)));
        setSearchParams(newParams);
    }

    const setView = (mode: ViewMode): void => updateFilter('view', mode === 'timeline' ? null : mode);

    const unassignedTours = tours.filter((tour) => tour.deliveryPersonUserId === null).length;

    /**
     * Appro options carrying the day's load — tours AND estimated hours. A delivery person over the 7 h day is
     * flagged here, before the dispatcher hands them a second tour.
     */
    const loads = useMemo(() => deliveryPersonDayLoads(tours), [tours]);

    const deliveryPersonOptions = useMemo<ApproOption[]>(
        () =>
            (peopleData?.getDeliveryPeople ?? [])
                .map((person) => {
                    const label = person.profile.fullname ?? person.username;
                    const load = loads.get(person._id);
                    if (!load) return { value: person._id, label };
                    const suffix = `${load.tours} tournée${load.tours > 1 ? 's' : ''} · ${formatMinutes(Math.round(load.minutes))}`;
                    return {
                        value: person._id,
                        label: `${label} — ${suffix}${load.isOverloaded ? ' ⚠' : ''}`,
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
        [peopleData, loads],
    );

    const addDraftTour = async (): Promise<void> => {
        let input = '';
        const hasConfirmed = await confirmPopup({
            title: 'Nouvelle tournée',
            message: 'Nom de la tournée',
            body: <TotemInput onChange={(value) => (input = value)} autoFocus dataTest="create-tour-name" />,
        });
        const name = input.trim();
        if (!hasConfirmed || !name) return;
        setDraftTours((current) => [...current, { key: `draft:${name}:${current.length}`, name }]);
        setView('timeline');
    };

    const actionSections = [
        {
            title: 'Autres actions',
            actions: [
                {
                    icon: <Plus />,
                    label: 'Nouvelle tournée',
                    onClick: () => void addDraftTour(),
                    requiredPermissions: [TotadminPermission.ToursManage],
                },
                {
                    icon: <MapPinPlus />,
                    label: 'Créer une livraison store',
                    onClick: () => setIsCreateSiteDeliveryPopupOpen(true),
                    requiredPermissions: [TotadminPermission.DeliveriesManage],
                },
                {
                    icon: <ClipboardPlus />,
                    label: 'Ajouter une tâche',
                    onClick: () => setOpenedTask('new'),
                },
            ],
        },
    ];

    const overloadedDeliveryPeople = useMemo(
        () => [...loads.values()].filter((load) => load.isOverloaded).length,
        [loads],
    );

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.tours} />
                </HeaderTitle>
                <HeaderActions>
                    <SegmentedControl<ViewMode>
                        options={[
                            { value: 'timeline', label: 'Timeline' },
                            { value: 'carte', label: 'Carte' },
                            { value: 'tableau', label: 'Tableau' },
                        ]}
                        value={view}
                        onChange={setView}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={actions.regenerate}
                        disabled={actions.building}
                        requiredPermissions={[TotadminPermission.ToursManage]}
                    >
                        {actions.building ? 'Régénération…' : 'Régénérer les tournées'}
                    </Button>
                    <DateNavigator selectedDate={selectedDate} onChange={setSelectedDate} />
                </HeaderActions>
            </Header>

            <Body>
                <Content>
                    <KpiStrip>
                        <Kpi>
                            <KpiValue>{tours.length}</KpiValue>
                            <KpiLabel>Tournées</KpiLabel>
                        </Kpi>
                        <Kpi $tone="unassigned">
                            <KpiValue>{unassignedTours}</KpiValue>
                            <KpiLabel>Sans appro</KpiLabel>
                        </Kpi>
                        <Kpi $tone={overloadedDeliveryPeople ? 'failed' : undefined} title={TOUR_LOAD_HINT}>
                            <KpiValue>{overloadedDeliveryPeople}</KpiValue>
                            <KpiLabel>Appros surchargés</KpiLabel>
                        </Kpi>
                        <Kpi>
                            <KpiValue>{kpis.total}</KpiValue>
                            <KpiLabel>Livraisons</KpiLabel>
                        </Kpi>
                        <Kpi $tone="inprogress">
                            <KpiValue>{kpis.inProgress}</KpiValue>
                            <KpiLabel>En cours</KpiLabel>
                        </Kpi>
                        <Kpi $tone="delivered">
                            <KpiValue>{kpis.delivered}</KpiValue>
                            <KpiLabel>Livrées</KpiLabel>
                        </Kpi>
                        <Kpi $tone="failed">
                            <KpiValue>{kpis.failed}</KpiValue>
                            <KpiLabel>Non livrées</KpiLabel>
                        </Kpi>
                        {allTasks.length > 0 && (
                            <Kpi $tone="delivered">
                                <KpiValue>
                                    {finishedTaskCount} / {allTasks.length}
                                </KpiValue>
                                <KpiLabel>Tâches faites</KpiLabel>
                            </Kpi>
                        )}
                    </KpiStrip>

                    {view === 'timeline' && (
                        <FilterBar
                            filters={FILTERS}
                            filterState={filterState}
                            updateFilter={updateFilter}
                            clearFilters={() => setSearchParams({})}
                            searchValue={searchValue}
                            onSearchChange={(value) => updateFilter('searchValue', value)}
                            searchPlaceholder="Rechercher par nom du site et organisation"
                            className="p-0"
                            actions={
                                matchingStopIds && (
                                    <MatchCount>
                                        {
                                            [...allDeliveries, ...allTasks].filter(({ _id }) =>
                                                matchingStopIds.has(_id),
                                            ).length
                                        }{' '}
                                        / {allDeliveries.length + allTasks.length} arrêts
                                    </MatchCount>
                                )
                            }
                        />
                    )}

                    <ViewArea>
                        {view === 'timeline' && (
                            <TourTimeline
                                tours={timelineTours}
                                unassignedDeliveries={unassignedDeliveries}
                                unassignedTasks={unassignedTasks}
                                onOpenTask={openTask}
                                isToday={formatDateAsAnniversary({ dateTime: new Date() }) === birthdayDate}
                                deliveryPersonOptions={deliveryPersonOptions}
                                actions={actions}
                                draftTours={draftTours}
                                setDraftTours={setDraftTours}
                                matchingStopIds={matchingStopIds}
                            />
                        )}
                        {view === 'carte' && (
                            <TourEditor
                                tours={tours}
                                unassignedDeliveries={unassignedDeliveries}
                                unassignedTasks={unassignedTasks}
                                deliveryPersonOptions={deliveryPersonOptions}
                                actions={actions}
                                focusedStopId={focusedStopId}
                                onFocusStop={setFocusedStopId}
                                onOpenTask={openTask}
                            />
                        )}
                        {view === 'tableau' && (
                            <TourList
                                tours={tours}
                                unassignedDeliveries={unassignedDeliveries}
                                unassignedTasks={unassignedTasks}
                            />
                        )}
                    </ViewArea>
                </Content>
                <ActionBar actionSections={actionSections} />
            </Body>
            <CreateSiteDeliveryPopup
                key={birthdayDate}
                isOpen={isCreateSiteDeliveryPopupOpen}
                setIsOpen={setIsCreateSiteDeliveryPopupOpen}
                defaultDate={selectedDate}
                onCreated={() => void refetch()}
            />
            {openedTask && (
                <TaskPopup
                    task={openedTask === 'new' ? null : openedTask}
                    birthdayDate={birthdayDate}
                    onClose={() => setOpenedTask(null)}
                    onSaved={() => void refetch()}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const Body = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    overflow: hidden;
`;

const Content = styled.div`
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
`;

const MatchCount = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.infoTextColor};
`;

// Fills the height left below the KPI strip; the timeline/editor stretch to fit and scroll internally.
const ViewArea = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const KpiStrip = styled.div`
    flex-shrink: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
`;

const Kpi = styled.div<{ $tone?: KpiTone }>`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 14px;
    border-radius: 10px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-left: 4px solid ${({ $tone, theme }) => ($tone ? KPI_TONE[$tone] : theme.ctaPrimaryColor)};
`;

const KpiValue = styled.span`
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
    color: ${({ theme }) => theme.textColor};
`;

const KpiLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.infoTextColor};
`;
